import React, { useMemo, useState } from 'react';
import { POSE_DETAILS } from './poseDetails.gen';
import { listPoses, listPosesMissingImages, poseThumb, POSE_SHORTCUTS } from './poseImages';
import PoseTermText, { PoseSanskritLink } from './PoseTermText';

const CATEGORIES = [
  { id: 'all', label: 'All categories' },
  { id: 'standing', label: 'Standing' },
  { id: 'seated', label: 'Seated' },
  { id: 'supine', label: 'Supine' },
  { id: 'prone', label: 'Prone' },
  { id: 'arm_leg_support', label: 'Arm & leg support' },
  { id: 'arm_balance_and_inversion', label: 'Arm balance & inversion' },
];

const DIFFICULTIES = [
  { id: 'all', label: 'Any level' },
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'expert', label: 'Expert' },
];

const VISIBILITIES = [
  { id: 'primary', label: 'Primary' },
  { id: 'all', label: 'All poses' },
  { id: 'secondary', label: 'Secondary' },
  { id: 'tertiary', label: 'Tertiary' },
];

const SHORTCUT_BY_NAME = Object.fromEntries(
  Object.entries(POSE_SHORTCUTS).map(([short, name]) => [name, short]),
);

function ChipRow({ options, value, onChange, label }) {
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <span className="u-xs u-muted">{label}</span>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} role="group" aria-label={label}>
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className="cs-chip"
            aria-pressed={value === opt.id}
            onClick={() => onChange(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PoseCard({ pose, selected, onSelect }) {
  const thumb = poseThumb(pose);
  const shortcut = SHORTCUT_BY_NAME[pose.name];
  return (
    <button
      type="button"
      className="yoga-pose-card"
      aria-pressed={selected}
      onClick={() => onSelect(pose)}
    >
      {thumb?.url ? (
        <img src={thumb.url} alt="" loading="lazy" />
      ) : (
        <div className="yoga-pose-card__placeholder" aria-hidden />
      )}
      <div className="yoga-pose-card__body">
        <div className="yoga-pose-card__title">{pose.displayName}</div>
        {pose.sanskrit?.[0] ? (
          <div className="u-muted u-small" style={{ fontStyle: 'italic' }}>
            <PoseSanskritLink pose={pose}>{pose.sanskrit[0]}</PoseSanskritLink>
          </div>
        ) : null}
        <div className="u-cluster" style={{ '--gap': 'var(--sp-1)', marginTop: 'var(--sp-1)' }}>
          <span className="cs-badge">{pose.difficulty}</span>
          {shortcut ? <span className="cs-badge">**Pose:** {shortcut}</span> : null}
        </div>
      </div>
    </button>
  );
}

function PoseDetail({ pose, onClose }) {
  if (!pose) return null;
  const thumb = poseThumb(pose);
  const detail = POSE_DETAILS[pose.name] || {};
  const shortcut = SHORTCUT_BY_NAME[pose.name];
  const cueKey = shortcut || pose.name;

  return (
    <aside className="yoga-pose-detail m-fade-in" aria-label={`${pose.displayName} details`}>
      <div className="u-split" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>{pose.displayName}</h2>
          {pose.sanskrit?.[0] ? (
            <p className="u-muted" style={{ margin: 'var(--sp-1) 0 0', fontStyle: 'italic' }}>
              <PoseSanskritLink pose={pose}>
                {pose.sanskrit.join(' · ')}
              </PoseSanskritLink>
            </p>
          ) : null}
        </div>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {thumb?.url ? (
        <figure className="yoga-pose" style={{ margin: 'var(--sp-4) 0 0', maxWidth: 360 }}>
          <img
            src={thumb.url}
            alt={thumb.alt}
            style={{ display: 'block', width: '100%', height: 280, objectFit: 'contain', background: '#fff' }}
          />
        </figure>
      ) : null}

      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
        <span className="cs-badge">{pose.category.replace(/_/g, ' ')}</span>
        <span className="cs-badge">{pose.difficulty}</span>
        <span className="cs-badge">{pose.visibility}</span>
      </div>

      <div
        className="cs-banner"
        style={{ marginTop: 'var(--sp-4)' }}
        role="note"
      >
        Use in lesson cues:{' '}
        <code>**Pose:** {cueKey}</code>
        {shortcut ? (
          <span className="u-muted u-small">
            {' '}(or full name <code>**Pose:** {pose.name}</code>)
          </span>
        ) : null}
      </div>

      {pose.aka?.length ? (
        <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--text)' }}>
          <span className="u-muted u-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Also known as
          </span>
          <br />
          {pose.aka.join(' · ')}
        </p>
      ) : null}

      {detail.description ? (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <div className="u-xs u-muted" style={{ letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 'var(--sp-1)' }}>
            How to
          </div>
          <p style={{ margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            <PoseTermText text={detail.description} />
          </p>
        </div>
      ) : null}

      {detail.benefits ? (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <div className="u-xs u-muted" style={{ letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 'var(--sp-1)' }}>
            Benefits
          </div>
          <p style={{ margin: 0, lineHeight: 1.55 }}>{detail.benefits}</p>
        </div>
      ) : null}
    </aside>
  );
}

export default function PoseDictionary({ embedded = false }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [difficulty, setDifficulty] = useState('all');
  const [visibility, setVisibility] = useState('primary');
  const [selectedName, setSelectedName] = useState(null);
  const [missingOnly, setMissingOnly] = useState(false);

  const missingImages = useMemo(() => listPosesMissingImages(), []);
  const missingNames = useMemo(
    () => new Set(missingImages.map((p) => p.name)),
    [missingImages],
  );

  const poses = useMemo(
    () => {
      const list = listPoses({
        query,
        category: category === 'all' ? undefined : category,
        difficulty: difficulty === 'all' ? undefined : difficulty,
        visibility: visibility === 'all' ? undefined : visibility,
      });
      return missingOnly ? list.filter((p) => missingNames.has(p.name)) : list;
    },
    [query, category, difficulty, visibility, missingOnly, missingNames],
  );

  const selected = useMemo(
    () => poses.find((p) => p.name === selectedName) || listPoses({}).find((p) => p.name === selectedName) || null,
    [poses, selectedName],
  );

  return (
    <div className={embedded ? 'm-fade-in' : 'yoga-page m-fade-in'}>
      <header className="u-stack" style={{ '--gap': 'var(--sp-2)', marginBottom: 'var(--sp-5)' }}>
        {embedded ? null : <h1 style={{ margin: 0 }}>Pose dictionary</h1>}
        <p className="u-muted" style={{ margin: 0, maxWidth: 640 }}>
          {POSE_INDEX_COUNT} Pocket Yoga stills. Search by English name,
          Sanskrit, or aka — then copy a pose key into the Teach sequence builder.
          Sanskrit names open a pop-up with the still and how-to.
        </p>
        {missingImages.length ? (
          <div className="cs-banner" role="status">
            {missingImages.length} pose{missingImages.length === 1 ? '' : 's'} missing image
            files:{' '}
            {missingImages
              .slice(0, 12)
              .map((p) => p.displayName)
              .join(', ')}
            {missingImages.length > 12 ? ` … +${missingImages.length - 12} more` : ''}
          </div>
        ) : null}
      </header>

      <div className="u-stack" style={{ '--gap': 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
        <label className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <span className="u-xs u-muted">Search</span>
          <input
            className="cs-input"
            type="search"
            placeholder="e.g. downward dog, navasana, warrior…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <ChipRow options={VISIBILITIES} value={visibility} onChange={setVisibility} label="Visibility" />
        <ChipRow options={CATEGORIES} value={category} onChange={setCategory} label="Category" />
        <ChipRow options={DIFFICULTIES} value={difficulty} onChange={setDifficulty} label="Difficulty" />
        {missingImages.length ? (
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <span className="u-xs u-muted">Assets</span>
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} role="group" aria-label="Assets">
              <button
                type="button"
                className="cs-chip"
                aria-pressed={!missingOnly}
                onClick={() => setMissingOnly(false)}
              >
                All with filters
              </button>
              <button
                type="button"
                className="cs-chip"
                aria-pressed={missingOnly}
                onClick={() => setMissingOnly(true)}
              >
                Missing images ({missingImages.length})
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <p className="u-muted u-small" style={{ margin: '0 0 var(--sp-3)' }}>
        Showing {poses.length} pose{poses.length === 1 ? '' : 's'}
      </p>

      <div className="yoga-pose-layout">
        <div className="yoga-pose-grid">
          {poses.map((pose) => (
            <PoseCard
              key={pose.name}
              pose={pose}
              selected={selectedName === pose.name}
              onSelect={(p) => setSelectedName(p.name)}
            />
          ))}
          {!poses.length ? (
            <p className="u-muted">No poses match these filters.</p>
          ) : null}
        </div>
        {selected ? (
          <PoseDetail pose={selected} onClose={() => setSelectedName(null)} />
        ) : null}
      </div>
    </div>
  );
}

// Avoid importing the full index again just for a count label.
const POSE_INDEX_COUNT = listPoses({}).length;
